// mobile/screens/LoginScreen.js
import React, { useState, useContext } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ActivityIndicator,
  KeyboardAvoidingView, Platform, StyleSheet, Alert, ScrollView, StatusBar
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import axios from 'axios';
import { API_BASE } from '../utils';                 // pastikan path benar
import { authStorage } from '../utils/authStorage';  // pastikan path benar
import { AuthContext } from '../App';                // <- ambil signIn dari context
import BrandMark from '../components/BrandMark';
import { PADIPRO, RADIUS, SPACE, padiproShadow, padiproShadowLg } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

export default function LoginScreen() {
  const { signIn } = useContext(AuthContext);       // <- pakai ini, bukan navigation.replace
  const { contentPadding, isTiny } = useResponsive();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secure, setSecure] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errText, setErrText] = useState('');
  const [focusField, setFocusField] = useState(null);

  const onLogin = async () => {
    if (loading) return;
    setErrText('');

    const e = (email || '').trim();
    if (!e || !password) {
      setErrText('Email dan password wajib diisi.');
      return;
    }

    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/login`, { email: e, password });
      const { token, user } = r.data || {};
      if (!token || !user) throw new Error('Response login tidak valid');

      // simpan session ke storage (opsional; signIn juga akan set state App)
      await authStorage.set('auth.token', token);
      await authStorage.set('auth.user', JSON.stringify(user));

      // trigger switch ke AppStack (tanpa navigation.replace)
      await signIn(token, user);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Login gagal';
      setErrText(msg);
      Alert.alert('Login gagal', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: PADIPRO.bg }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <StatusBar barStyle="dark-content" backgroundColor={PADIPRO.bg} />
      <ScrollView
        contentContainerStyle={[s.wrap, { padding: contentPadding }]}
        keyboardShouldPersistTaps="handled"
        contentInsetAdjustmentBehavior="automatic"
        showsVerticalScrollIndicator={false}
      >
        <View style={[s.card, isTiny && s.cardTiny]}>
          <View style={s.goldTopBar} />
          <View style={s.brandBlock}>
            <View style={s.brandPill}>
              <View style={s.brandPillDot} />
              <Text style={s.brandPillText}>Enterprise Farming Suite</Text>
            </View>
            <BrandMark size={58} title="PanenKu" subtitle="AI Crop Intelligence" />
            <Text style={s.title}>Masuk ke Command Center</Text>
            <Text style={s.subtitle}>Kelola stok, operasional, dan pemindaian produk.</Text>
          </View>

          {Boolean(errText) && (
            <View style={s.errorBox} accessibilityRole="alert">
              <Ionicons name="alert-circle" size={16} color={PADIPRO.danger} />
              <Text style={s.errorText} numberOfLines={3}>{errText}</Text>
            </View>
          )}

          <View style={s.field}>
            <Text style={s.label}>Email</Text>
            <TextInput
              value={email}
              onChangeText={setEmail}
              placeholder="you@example.com"
              placeholderTextColor={PADIPRO.mutedSoft}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              textContentType="emailAddress"
              returnKeyType="next"
              onFocus={() => setFocusField('email')}
              onBlur={() => setFocusField(null)}
              style={[s.input, focusField === 'email' && s.inputFocused]}
            />
          </View>

          <View style={s.field}>
            <Text style={s.label}>Password</Text>
            <View style={[s.inputRow, focusField === 'password' && s.inputFocused]}>
              <TextInput
                value={password}
                onChangeText={setPassword}
                placeholder="••••••••"
                placeholderTextColor={PADIPRO.mutedSoft}
                secureTextEntry={secure}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="password"
                textContentType="password"
                returnKeyType="done"
                onSubmitEditing={onLogin}
                onFocus={() => setFocusField('password')}
                onBlur={() => setFocusField(null)}
                style={[s.input, s.inputInRow]}
              />
              <TouchableOpacity
                onPress={() => setSecure(v => !v)}
                style={s.eyeBtn}
                activeOpacity={0.7}
                disabled={loading}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel={secure ? 'Tampilkan password' : 'Sembunyikan password'}
              >
                <Ionicons name={secure ? 'eye-outline' : 'eye-off-outline'} size={20} color={PADIPRO.primary} />
              </TouchableOpacity>
            </View>
          </View>

          <TouchableOpacity
            onPress={onLogin}
            disabled={loading}
            style={[s.btnPrimary, loading && { opacity: 0.85 }]}
            activeOpacity={0.9}
            accessibilityRole="button"
          >
            {loading ? (
              <ActivityIndicator color="#ffffff" />
            ) : (
              <>
                <Text style={s.btnPrimaryText}>Masuk</Text>
                <Ionicons name="arrow-forward" size={18} color="#ffffff" style={{ marginLeft: 8 }} />
              </>
            )}
          </TouchableOpacity>

          <Text style={s.hint}>
            Sesi akan tetap aktif hingga 30 hari. Pastikan perangkat aman.
          </Text>
        </View>

        <View style={s.footerRow}>
          <View style={s.footerDivider} />
          <Text style={s.footerNote}>© {new Date().getFullYear()} PanenKu</Text>
          <View style={s.footerDivider} />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const s = StyleSheet.create({
  wrap: { flexGrow: 1, padding: SPACE.lg + 2, justifyContent: 'center', backgroundColor: PADIPRO.bg },
  card: {
    position: 'relative',
    backgroundColor: PADIPRO.surface,
    borderRadius: RADIUS.xl + 4,
    padding: SPACE.xl,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: PADIPRO.border,
    ...padiproShadowLg,
  },
  cardTiny: {
    padding: SPACE.lg,
  },
  goldTopBar: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 4,
    backgroundColor: PADIPRO.gold,
  },
  brandBlock: { alignItems: 'center', marginBottom: SPACE.md + 2, marginTop: SPACE.xs },
  brandPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACE.md - 1,
    paddingVertical: 5,
    borderRadius: RADIUS.pill,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    backgroundColor: PADIPRO.goldSoft,
    marginBottom: SPACE.md - 2,
  },
  brandPillDot: {
    width: 5, height: 5, borderRadius: 3, backgroundColor: PADIPRO.gold, marginRight: 6,
  },
  brandPillText: { color: PADIPRO.goldDeep, fontSize: 11, fontWeight: '800', letterSpacing: 0.3 },
  title: { fontSize: 23, fontWeight: '800', color: PADIPRO.textStrong, marginTop: 14, textAlign: 'center' },
  subtitle: { color: PADIPRO.muted, marginTop: 4, marginBottom: SPACE.sm, fontSize: 13, textAlign: 'center', lineHeight: 18 },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: PADIPRO.dangerSoft,
    borderWidth: 1,
    borderColor: 'rgba(159, 33, 56, 0.25)',
    borderRadius: RADIUS.sm,
    padding: SPACE.sm + 2,
    marginBottom: SPACE.md,
  },
  errorText: { color: PADIPRO.danger, fontSize: 13, flex: 1 },
  field: { marginBottom: SPACE.md + 2 },
  label: { fontWeight: '700', color: PADIPRO.text, marginBottom: 6, letterSpacing: 0.1, fontSize: 13 },
  input: {
    borderWidth: 1, borderColor: PADIPRO.border, borderRadius: RADIUS.md,
    paddingHorizontal: SPACE.md, paddingVertical: 13, backgroundColor: PADIPRO.surfaceSunken,
    color: PADIPRO.text, fontSize: 15, minHeight: 48,
  },
  inputFocused: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.surface },
  inputRow: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderColor: PADIPRO.border, borderRadius: RADIUS.md,
    backgroundColor: PADIPRO.surfaceSunken, paddingRight: SPACE.sm,
  },
  inputInRow: { flex: 1, borderWidth: 0, backgroundColor: 'transparent', minHeight: 48 },
  eyeBtn: {
    width: 36, height: 36, borderRadius: RADIUS.sm,
    alignItems: 'center', justifyContent: 'center',
  },
  btnPrimary: {
    marginTop: SPACE.sm, backgroundColor: PADIPRO.primary, paddingVertical: 15, borderRadius: RADIUS.md,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    ...padiproShadow,
  },
  btnPrimaryText: { color: '#ffffff', fontWeight: '800', letterSpacing: 0.3, fontSize: 16 },
  hint: { textAlign: 'center', color: PADIPRO.mutedSoft, marginTop: SPACE.md, fontSize: 12, lineHeight: 17 },
  footerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: SPACE.xl },
  footerDivider: { width: 24, height: 1, backgroundColor: PADIPRO.borderStrong },
  footerNote: { color: PADIPRO.mutedSoft, fontSize: 12, fontWeight: '600' },
});
